import type { CustomerDTO } from "@ifa/utils";

type CustomerFinancial = CustomerDTO["financial"] | null | undefined;

export type ZuuFinancialInfoDisplay = {
  monthlyIncomeLast24Months?: number;
  monthlyExpenseLast24Months?: number;
  monthlyDisposableIncomeAfterRetirement?: number;
  currentLiquidAssets?: number;
  additionalLiquidAssetsAfterRetirement?: number;
};

function toNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function sumDefined(values: Array<number | undefined>): number | undefined {
  const definedValues = values.filter(
    (value): value is number => value !== undefined,
  );

  if (definedValues.length === 0) {
    return undefined;
  }

  return definedValues.reduce((total, value) => total + value, 0);
}

export function resolveZuuFinancialInfoDisplay(
  financial: CustomerFinancial,
): ZuuFinancialInfoDisplay {
  if (!financial) {
    return {};
  }

  return {
    monthlyIncomeLast24Months:
      toNumber((financial as any).monthlyIncomesTotal) ??
      toNumber(financial.income?.avgMonthlyIncome) ??
      sumDefined([
        toNumber(financial.income?.salaryCommissionBonus),
        toNumber(financial.income?.rentalIncome),
        financial.income?.hasOtherIncome
          ? toNumber(financial.income?.otherIncomeAmount)
          : undefined,
      ]),
    monthlyExpenseLast24Months:
      toNumber((financial as any).monthlyExpensesTotal) ??
      toNumber(financial.expense?.avgMonthlyExpense) ??
      sumDefined([
        toNumber(financial.expense?.personalAndFamilyExpense),
        toNumber(financial.expense?.mortgageLoanRepayment),
        toNumber(financial.expense?.insuranceContribution),
        financial.expense?.hasOtherExpense
          ? toNumber(financial.expense?.otherExpenseAmount)
          : undefined,
      ]),
    monthlyDisposableIncomeAfterRetirement: toNumber(
      financial.income?.expectedRetireIncome,
    ),
    currentLiquidAssets:
      toNumber((financial as any).liquidAssetsTotal) ??
      toNumber(financial.currentAssets?.currentLiquidAssets) ??
      sumDefined([
        toNumber(financial.currentAssets?.cashAndDeposits),
        toNumber(financial.currentAssets?.liquidInvestments),
        financial.currentAssets?.hasOtherCurrentAssets
          ? toNumber(financial.currentAssets?.otherCurrentAssetsAmount)
          : undefined,
      ]),
    additionalLiquidAssetsAfterRetirement: toNumber(
      financial.currentAssets?.expectedRetireLiquidAssets,
    ),
  };
}
